import {AfterViewInit, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild} from '@angular/core';
import {UntypedFormControl} from "@angular/forms";
import {merge, of} from "rxjs";
import {MatPaginator} from "@angular/material/paginator";
import {catchError, map, startWith, switchMap} from "rxjs/operators";
import {HttpParams} from "@angular/common/http";
import {CategoryService} from "../../../services/vocab/category.service";
import {NotificationService} from "../../../services/common/notification.service";
import {SelectionModel} from "@angular/cdk/collections";
import {Type} from "../../../shared/Type";
import {WordService} from "../../../services/vocab/word.service";
import {Word} from "../../../dtos/vocab/entry/word";
import {SysGrammarService} from "../../../services/grammar/sys-grammar.service";
import {FuncGrammarService} from "../../../services/grammar/func-grammar.service";
import {TextPatternService} from "../../../services/text/text-pattern.service";
import {TextTypeService} from "../../../services/text/text-type.service";
import {DetailedCanDoStatementService} from "../../../services/can-do-statement/detailed-can-do-statement.service";
import {DetailedCanDoStatementCategoryService} from "../../../services/can-do-statement/detailed-can-do-statement-category.service";
import {GlobalCanDoStatementService} from "../../../services/can-do-statement/global-can-do-statement.service";
import {GlobalCanDoStatementCategoryService} from "../../../services/can-do-statement/global-can-do-statement-category.service";
import {VerbalActionCategoryService} from "../../../services/verbal-action/verbal-action-category.service";

// TODO check with Max


@Component({
    selector: 'app-simple-select',
    templateUrl: './simple-select.component.html',
    styleUrl: './simple-select.component.scss',
    standalone: false
})
export class SimpleSelectComponent implements OnChanges, AfterViewInit {

  @ViewChild(MatPaginator) paginator: MatPaginator;

  @Input() selectionMode: string;
  @Input() selectedObject: any;
  @Input() selectedObjects: any[];
  @Input() displayedColumns: string[];
  @Input() excludedId: number;
  @Input() dialogTitle: string;
  @Input() selectionEntity: any;

  @Output() selectedObjectOutput = new EventEmitter<any>()


  searchControl = new UntypedFormControl('');
  selection: SelectionModel<any>


  matchingData: any[];
  isLoadingResults = true;
  isRateLimitReached = false;

  resultsLength = 25;


  constructor(private categoryService: CategoryService,
              private wordService: WordService, private funcGrammarService: FuncGrammarService,
              private sysGrammarService: SysGrammarService, private textPatternService: TextPatternService,
              private notificationService: NotificationService, private textTypeService: TextTypeService,
              private detailedCanDoStatementService: DetailedCanDoStatementService,
              private detailedCanDoStatementCategoryService: DetailedCanDoStatementCategoryService,
              private globalCanDoStatementService: GlobalCanDoStatementService,
              private globalCanDoStatementCategoryService: GlobalCanDoStatementCategoryService,
              private verbalActionCategoryService: VerbalActionCategoryService) {
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['selectionMode']) {
      this.selectionMode = changes['selectionMode'].currentValue
      if (this.selectionMode === 'multi-select') {
        this.selection = new SelectionModel(true, [])
      } else if (this.selectionMode === 'single-select') {
        this.selection = new SelectionModel(false, [])
      }
    }

    if (changes['selectionClear'] && !changes['selectionClear'].isFirstChange()) {
      this.selection.clear()
    }
    if (changes['selectedObject'] && !changes['selectedObject'].isFirstChange()) {
      this.selectedObject = changes['selectedObject'].currentValue
      if (this.selectedObject === null) {
        this.selection.clear()
      }
    }
  }

  ngAfterViewInit() {
    this.search()
  }


  search() {
    merge(this.paginator.page)
      .pipe(
        startWith({}),
        switchMap(() => {
          this.isLoadingResults = true;
          let params = new HttpParams();
          params = params.append('name', String((this.searchControl.value == null) ? '' : this.searchControl.value));
          params = params.append('size', String(this.paginator.pageSize));
          params = params.append('page', String(this.paginator.pageIndex));
          if (this.excludedId) {
            params = params.append('exclude', this.excludedId);
          }
          if (this.selectionEntity === 'vocab_category') {
            return this.categoryService.searchCategories(params)
              .pipe(catchError(() => of(null)));
          }
          if (this.selectionEntity === 'vocab_entry') {
            return this.wordService.searchEntries(params)
              .pipe(catchError(() => of(null)));
          }
          if (this.selectionEntity === 'sys_category') {
            return this.sysGrammarService.searchCategories(params)
              .pipe(catchError(() => of(null)));
          }
          if (this.selectionEntity === 'sys_entry') {
            return this.sysGrammarService.searchEntries(params)
              .pipe(catchError(() => of(null)));
          }
          if (this.selectionEntity === 'fun_category') {
            return this.funcGrammarService.searchCategories(params)
              .pipe(catchError(() => of(null)));
          }
          if (this.selectionEntity === 'fun_entry') {
            return this.funcGrammarService.searchEntries(params)
              .pipe(catchError(() => of(null)));
          }
          if (this.selectionEntity === 'text_pattern') {
            return this.textPatternService.searchTextPatterns(params)
              .pipe(catchError(() => of(null)));
          }
          if (this.selectionEntity === 'text_type') {
            return this.textTypeService.searchEntries(params)
              .pipe(catchError(() => of(null)));
          }
          if (this.selectionEntity === 'det_can_statements') {
            return this.detailedCanDoStatementService.searchEntries(params)
              .pipe(catchError(() => of(null)));
          }
          if (this.selectionEntity === 'det_can_statements_category') {
            return this.detailedCanDoStatementCategoryService.searchCategories(params)
              .pipe(catchError(() => of(null)));
          }
          if (this.selectionEntity === 'glob_can_statements') {
            return this.globalCanDoStatementService.searchEntries(params)
              .pipe(catchError(() => of(null)));
          }
          if (this.selectionEntity === 'glob_can_statements_category') {
            return this.globalCanDoStatementCategoryService.searchCategories(params)
              .pipe(catchError(() => of(null)));
          }
          if (this.selectionEntity === 'va_category') {
            return this.verbalActionCategoryService.searchCategories(params)
              .pipe(catchError(() => of(null)));
          }
        }),
        map(data => {
          // Flip flag to show that loading has finished.
          this.isLoadingResults = false;
          this.isRateLimitReached = data === null;

          if (data === null) {
            return [];
          }
          // Only refresh the result length if there is new data. In case of rate
          // limit errors, we do not want to reset the paginator to zero, as that
          // would prevent users from re-triggering requests.
          this.resultsLength = data.totalElements;
          return data.content;
        })
      ).subscribe(data => {
      this.matchingData = data;
      this.selection.clear();

      if (this.selectionMode === 'single-select') {
        if (this.selectedObject !== undefined) {
          const i = this.getObjectWithId(this.selectedObject?.id);
          if (i !== null) {
            this.selection.select(this.getObjectWithId(this.selectedObject?.id));
          }
        }
      }

      if (this.selectionMode === 'multi-select') {
        this.selectedObjects.forEach(o => {
          const i = this.getObjectWithId(o.id);
          if (i !== null) {
            this.selection.select(this.getObjectWithId(o.id));
          }
        })
      }
    }, error => {
      this.notificationService.openSnackBar(error);
    });
  }

  getObjectWithId(id: number) {
    let v = null;
    this.matchingData.forEach(c => {
      if (c.id === id) {
        v = c;
      }
    });
    return v;
  }

  changeSelection(row?: any) {
    this.selection.toggle(row);
    if (!this.selection.isSelected(row)) {
      this.selectedObject = null;
      if (this.selectionMode === 'multi-select') {
        this.selectedObjects = this.selectedObjects.filter(obj => obj.id !== row.id);
      }
    } else {
      this.selectedObject = row;
      if (this.selectionMode === 'multi-select') {
        this.selectedObjects.push(row);
      }
    }
    this.emitSelectedObjects()
  }

  typeEnumToString(type: Type): string {
    if (type == null) {
      return '';
    } else {
      return Type[type];
    }
  }

  /** Selects all rows if they are not all selected; otherwise clear selection. */
  toggleAllRows() {
    if (this.isAllSelected()) {
      this.selection.selected.forEach(s => {
        this.selectedObjects = this.selectedObjects.filter(w => w.id !== s.id);
      });
      this.selection.clear();
      this.emitSelectedObjects()
      return;
    }

    this.matchingData.forEach(w => {
      if (!this.selection.isSelected(w)) {
        this.selection.select(w);
        this.selectedObjects.push(w);
      }
    });
    this.emitSelectedObjects()

  }

  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.paginator?.pageSize;
    return (numSelected === numRows) || (numSelected > numRows);
  }


  /** The label for the checkbox on the passed row */
  checkboxLabel(row?: Word): string {
    if (!row) {
      return `${this.isAllSelected() ? 'deselect' : 'select'} all`;
    }
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row ${row.id}`;
  }

  deselectObject() {
    this.selectedObject = null;
    this.selectedObjects = [];
    this.selection.clear()
    this.emitSelectedObjects()
  }

  emitSelectedObjects() {
    if (this.selectionMode === 'single-select') {
      this.selectedObjectOutput.emit({type: 'single', data: this.selectedObject})
    } else if (this.selectionMode === 'multi-select') {
      this.selectedObjectOutput.emit({type: 'multi', data: this.selectedObjects})
    }
  }

  getMode(element) {
    if (element.mode === 'ORAL') {
      return "mündlich";
    } else if (element.mode === 'WRITTEN') {
      return "schriftlich";
    }
    return "";
  }
}
